import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';

import React, { useCallback, useMemo } from 'react';
import classnames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

import { Checkbox } from 'ui-components/components/checkbox';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { NumberInput } from 'ui-components/components/number-input';

import styles from './styles.module.less';
import { useAddFundsContext } from '../../context/add-funds-context-provider';
import { AddFundsErrorSection } from '../add-funds-modal/error-section';
import { MINIMUM_PURCHASE_AMT, DEFAULT_AUTO_FUND_AMTS } from '../constants/add-funds-constants';
import { AddFundButtons } from '../funding/add-fund-buttons';
import messages from '../funding/messages';

/**
 *
 * @param {object} props
 * @param {string} props.currencyCode
 * @param {(isDisabled: boolean) => void} props.setSaveIsDisabled
 * @param {boolean} props.saveIsDisabled
 * @returns {JSX.Element}
 */
export const AutoFundModalBody = ({ currencyCode, setSaveIsDisabled, saveIsDisabled }) => {
  const {
    autoFundEnabled,
    balanceThreshold,
    setBalanceThreshold,
    autoFundAmount,
    setAutoFundAmount,
    maxDailyFundings,
    setMaxDailyFundings,
    submitting,
    autoFundErrors: errors,
    updateAutoFundEnabled,
  } = useAddFundsContext();

  const intl = useIntl();
  const isBelowMinimumValue = autoFundAmount < MINIMUM_PURCHASE_AMT;
  const thresholdError = useMemo(() => {
    if (balanceThreshold < 0) {
      return [messages.minimumThresholdFundsError];
    }
    if (!balanceThreshold || balanceThreshold === 'NaN') {
      return [messages.requiredThresholdFundsError];
    }
    return [];
  }, [balanceThreshold]);

  const updateAutoFundAmount = useCallback(
    (value) => {
      setAutoFundAmount(value);
      if (value < MINIMUM_PURCHASE_AMT && !saveIsDisabled) {
        setSaveIsDisabled(true);
      }
      if (value >= MINIMUM_PURCHASE_AMT && saveIsDisabled) {
        setSaveIsDisabled(false);
      }
    },
    [saveIsDisabled, setAutoFundAmount, setSaveIsDisabled],
  );

  const setAutoFundThreshold = useCallback(
    (value) => {
      setBalanceThreshold(value);
      if (value < 0 || value === 'NaN') {
        setSaveIsDisabled(true);
      } else {
        setSaveIsDisabled(false);
      }
    },
    [setBalanceThreshold, setSaveIsDisabled],
  );

  const onArrowButtonPress = useCallback(
    (updatedValue) => {
      setMaxDailyFundings(updatedValue);
    },
    [setMaxDailyFundings],
  );

  const inputsDisabled = !autoFundEnabled || submitting;
  return (
    <div className={styles.body}>
      {errors?.length > 0 && <AddFundsErrorSection currencyCode={currencyCode} errors={errors} />}
      <div className={styles.title}>
        <FormattedMessage {...messages.autoFundBalance} />
      </div>
      <Checkbox checked={autoFundEnabled} disabled={submitting} onChange={updateAutoFundEnabled}>
        <span className={styles.label}>
          <FormattedMessage {...messages.enableAutoFunding} />
        </span>
      </Checkbox>
      <div className={styles['amount-note']}>
        <FormattedMessage {...messages.addFundsNote} />
      </div>
      <AddFundButtons
        classNames={classnames(styles['other-amount-input'], {
          [styles['error-border']]: isBelowMinimumValue,
        })}
        disabled={!autoFundEnabled}
        fundAmount={autoFundAmount}
        onChangeAction={updateAutoFundAmount}
        onClickAction={updateAutoFundAmount}
        sellerCurrencyCode={currencyCode}
        values={DEFAULT_AUTO_FUND_AMTS}
      />

      <div className={styles['amount-note']}>
        <FormattedMessage {...messages.balanceThreshold} />
      </div>
      <div>
        <CurrencyInput
          className={classnames(styles.currency, {
            [styles['disabled-inputs']]: inputsDisabled,
            [styles['error-border']]: thresholdError.length > 0,
          })}
          currencyCode={currencyCode}
          data-testid={intl.formatMessage(messages.balanceThreshold)}
          disabled={inputsDisabled}
          onChange={setAutoFundThreshold}
          value={balanceThreshold}
        />
        {thresholdError.length > 0 && (
          <span className={styles['amount-error']}>
            <FontAwesomeIcon className={styles['error-icon']} icon={faExclamationCircle} />
            <FormattedMessage {...thresholdError[0]} />
          </span>
        )}
      </div>
      <div className={styles['amount-note']}>
        <FormattedMessage {...messages.maxFunding} />
      </div>
      <div className={styles['number-input-container']}>
        <NumberInput
          className={classnames(styles['number-input'], {
            [styles['disabled-inputs']]: inputsDisabled,
          })}
          disabled={inputsDisabled}
          min={1}
          onArrowButtonPress={onArrowButtonPress}
          onChange={setMaxDailyFundings}
          testId={intl.formatMessage(messages.maxFunding)}
          value={maxDailyFundings}
        />
        <span className={styles.label}>
          <FormattedMessage {...messages.timesPerDay} />
        </span>
      </div>
    </div>
  );
};
